import {
  CSSProperties,
  forwardRef,
  HTMLAttributes,
  ReactNode,
  useImperativeHandle,
  useRef,
} from 'react';
import { useLiquidGlass } from '../lib/useLiquidGlass';
import { GlassVariantKey, LiquidGlassConfig } from '../lib/liquidGlass';

export type GlassVariant = GlassVariantKey;

interface GlassPanelProps extends HTMLAttributes<HTMLDivElement> {
  variant?: GlassVariant;
  /** Apply the entrance rise animation. Defaults to true. */
  animate?: boolean;
  /** Optional config overrides on top of the variant preset. */
  glassOverride?: Partial<LiquidGlassConfig>;
  /** Disable the SVG filter (renders the panel without the displacement). */
  disableLiquid?: boolean;
  /** @deprecated kept for backward compat — no-op now (specular is in the SVG filter). */
  shimmer?: boolean;
  children?: ReactNode;
  style?: CSSProperties;
}

/**
 * Reusable liquid-glass surface backed by a per-element SVG filter
 * (feGaussianBlur → feImage(displacement) → feDisplacementMap →
 * feImage(specular) → feComposite / feBlend). Two child layers
 * (`mirror` for the page-bg copy that gets `filter: url(#id)`, `tint`
 * for color + inset shadow) are React-rendered into the panel; the
 * `useLiquidGlass` hook generates the SVG filter and applies it to
 * the mirror.
 *
 * Variants tune glass thickness, bezel width, blur, specular intensity,
 * and tint. Use `hero`/`menu` for floating overlays, `subtle` for nested
 * surfaces, `magenta` for accent panels.
 */
export const GlassPanel = forwardRef<HTMLDivElement, GlassPanelProps>(
  function GlassPanel(
    {
      variant = 'default',
      animate = true,
      glassOverride,
      disableLiquid,
      shimmer: _shimmer,
      className = '',
      children,
      style,
      ...rest
    },
    forwardedRef,
  ) {
    const innerRef = useRef<HTMLDivElement>(null);
    const mirrorRef = useRef<HTMLDivElement>(null);
    const tintRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(forwardedRef, () => innerRef.current as HTMLDivElement);

    // The frosted-glass surface is now painted entirely in CSS
    // (`.liquid-glass` uses backdrop-filter + a translucent fill + rim +
    // highlight — the documented "liquid glass" design standard). The old
    // per-element SVG displacement mirror rendered faint, mis-sized its
    // frosted region against a stale height (so panel content could sit on
    // un-frosted background), and attached a scroll listener per panel.
    // We keep the hook call but force it disabled so it never attaches;
    // the mirror/tint layer divs stay in the tree (hidden via CSS) purely
    // for backward compatibility with the existing markup.
    useLiquidGlass({
      ref: innerRef,
      mirrorRef,
      tintRef,
      variant,
      override: glassOverride,
      disabled: true,
    });
    void disableLiquid;

    const cls = [
      'liquid-glass',
      `liquid-glass--${variant}`,
      animate ? 'glass-rise' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div ref={innerRef} className={cls} style={style} {...rest}>
        {/* React-owned glass layers — paint order back → front:
              1. .lg-mirror — bg-attachment:fixed copy of var(--page-bg)
                              that lines up with body bg in viewport
                              coords. The hook applies inline
                              `filter: url(#id)` here so the SVG
                              displacement chain bends the gradient/grid
                              at the bezel and stamps the rim highlight.
                              SourceGraphic is the local bg-image, so we
                              don't depend on `backdrop-filter` (which
                              Chrome refuses to paint with url()).
              2. .lg-tint   — flat color tint + inset shadow on top.
                              Empty for the codepen-baseline `default`
                              variant; populated for brand variants
                              (magenta, hero, menu).
            Both sit absolutely-positioned at z:0 with overflow:hidden
            so the filtered output stays inside the panel's rounded
            corners. Sibling content gets z:1 via CSS so the panel can
            still use flex/grid for its layout. */}
        <div ref={mirrorRef} className="lg-layer lg-mirror" aria-hidden="true" />
        <div ref={tintRef} className="lg-layer lg-tint" aria-hidden="true" />
        {children}
      </div>
    );
  },
);
